/* global window */

import log from 'loglevel';
import loadJS from 'load-js';
import get from 'lodash/get';
import store from 'store/dist/store.modern';

import settings from './settings';
import { getParameterByName } from './utils';

/**
 * Class representing Braze.
 */
class Braze {
  /**
   * Create a Braze instance.
   * @param {Object} params - Braze configuration parameters.
   */
  constructor(parms) {
    this.parms = parms;
    this.currentUserData = {};
    this.lastPushRequestKey = 'MetaphorLastPushRequest';
  }

  /**
   * Check if Braze global script is ready.
   * @returns {Boolean}
   */
  get isBrazeReady() {
    return typeof window.braze !== 'undefined';
  }

  /**
   * Load Braze global script.
   * @returns {Promise}
   */
  load() {
    const scriptsFound = typeof get(settings, 'braze.scripts') !== 'undefined';
    const apiKeyFound = typeof get(settings, 'braze.apiKey') !== 'undefined';

    if (!scriptsFound || !apiKeyFound) {
      log.debug('Braze settings not found.');
      return Promise.resolve();
    }

    const scripts = get(settings, 'braze.scripts', []);
    const apiKey = get(settings, 'braze.apiKey', '');
    const loadDelay = get(settings, 'braze.loadDelay', 0);

    if (scripts.length !== 1 || !apiKey.length) {
      log.debug('Invalid Braze settings.');
      return Promise.resolve();
    }

    return new Promise(resolve => {
      setTimeout(
        () =>
          loadJS({
            url: settings.braze.scripts,
            async: true,
          }).then(resolve),
        loadDelay,
      );
    });
  }

  /**
   * Initialize Braze global script.
   */
  init() {
    if (!this.isBrazeReady) {
      log.error('Tried to initialize Braze before the script was ready.');
      return;
    }

    window.braze.initialize(settings.braze.apiKey, {
      baseUrl: 'sdk.iad-01.braze.com',
      doNotLoadFontAwesome: true,
      allowUserSuppliedJavascript: settings.braze.allowUserSuppliedJavascript,
      safariWebsitePushId: settings.braze.safariWebsitePushId,
    });

    window.braze.automaticallyShowInAppMessages();

    log.debug('Braze initialized.');
  }

  /**
   * Fetches user ID.
   * @param {boolean} extractIdFromQueryString - Whether to extract user ID from query string.
   * @returns {Promise}
   */
  fetchCurrentUserData(extractIdFromQueryString = false) {
    return new Promise(resolve => {
      if (extractIdFromQueryString) {
        const userId = getParameterByName('user_id');
        this.currentUserData.userId = userId;
        return resolve(userId);
      }

      const userId = window.braze.getUser().getUserId();
      if (userId) {
        log.debug(`Braze userId = [${userId}]`);
        this.currentUserData.userId = userId;
        return resolve(userId);
      }

      return resolve(null);
    });
  }

  /**
   * Return user ID.
   * @returns {String}
   */
  get userId() {
    return this.currentUserData.userId;
  }

  /**
   * Open Braze session.
   * @returns {Promise}
   */
  openSession() {
    const appEmbeddedQueryParam = getParameterByName('appembedded');
    if (appEmbeddedQueryParam == null) {
      window.braze.openSession();
      log.debug('Braze session opened.');

      const siteId = get(settings, 'site_id', 'vl');
      const eventName = `siteVisit.${siteId}`;
      window.braze.logCustomEvent(eventName);
      log.debug(`Braze custom event [${eventName}] triggered.`);
    }
    return Promise.resolve();
  }

  /**
   * Change user ID.
   * @param {String} userId - SHA-256 hash of user's email.
   * @returns {Promise}
   */
  changeUser(userId) {
    if (!userId) {
      return Promise.resolve();
    }

    window.braze.changeUser(userId);
    log.debug(`Braze userId changed [${userId}]`);

    return Promise.resolve();
  }

  /**
   * Check if push prompt can be triggered.
   * @returns {Boolean}
   */
  get canRequestPushPermission() {
    if (!store.enabled) {
      log.error('Storage not available.');
      return true;
    }

    const lastRequest = store.get(this.lastPushRequestKey);
    if (typeof lastRequest === 'undefined') {
      return true;
    }

    return Date.now() - lastRequest > settings.sessionLength;
  }

  /**
   * Requests push permission.
   * @returns {Promise}
   */
  pushRequest() {
    if (!window.braze.isPushSupported()) {
      log.error('Push not supported.');
      return Promise.resolve();
    }

    if (window.braze.isPushBlocked()) {
      log.debug('Push blocked.');
      return Promise.resolve();
    }

    if (window.braze.isPushPermissionGranted()) {
      log.debug('Push already granted.');
      return Promise.resolve();
    }

    if (!this.canRequestPushPermission) {
      log.debug('Cannot request push permission yet.');
      return Promise.resolve();
    }

    return new Promise(resolve => {
      log.debug('Requesting Braze push permission.');

      store.set(this.lastPushRequestKey, Date.now());

      window.braze.requestPushPermission(
        () => {
          log.debug('Braze push request granted.');
          resolve();
        },
        () => {
          log.debug('Braze push request denied.');
          resolve();
        },
      );
    });
  }

  /**
   * Set user last known location.
   * @param {Object} location - User location object.
   * @param {Object} neighborhoods - User neighborhoods object.
   * @returns {Promise}
   */
  setLastKnownLocation(location, neighborhoods) {
    if (location && location.length) {
      const { lat, lon } = location[0];
      const user = window.braze.getUser();
      if (user) {
        user.setLastKnownLocation(lat, lon);
        log.debug('Braze set last known location.');
      }
    }

    if (neighborhoods && neighborhoods.length) {
      neighborhoods.forEach(neighborhood => {
        const eventName = `Neighborhood ${neighborhood.name}`;
        window.braze.logCustomEvent(eventName);
        log.debug(`Braze custom event [${eventName}] triggered.`);
      });
    }

    return Promise.resolve();
  }

  /**
   * Set user and send subscribe event.
   * @param {String} userId - new User id.
   * @param {String} email - User email.
   * @param {string} eventName - subscription eventName.
   * @returns {Promise}
   */
  userSubscribe(userId, email, eventName) {
    if (!userId) {
      log.error('Subscribe failed. No user id provided');
      return Promise.resolve();
    }

    window.braze.changeUser(userId);
    log.debug(`Braze userId changed [${userId}]`);

    const user = window.braze.getUser();

    if (user && email) {
      user.setEmail(email);
    }

    if (eventName) {
      log.debug(`Braze logging custom event [${eventName}]`);

      const subscribed = window.braze.User.NotificationSubscriptionTypes.SUBSCRIBED;
      user.setEmailNotificationSubscriptionType(subscribed);

      window.braze.logCustomEvent(eventName);
      window.braze.requestImmediateDataFlush();
      log.debug('Braze subscribe success');
    }

    return Promise.resolve();
  }

  /**
   * Set user and subscribe to newsletter.
   * @param {Object} parameters
   * @param {String} parameters.userId - new User id.
   * @param {String} parameters.email - User email.
   * @param {String} parameters.groupId - Id of list to sign up for.
   * @param {String} parameters.source - The source from where the user signed up.
   * @param {String} parameters.homeCity - User home city.
   * @returns {Promise}
   */
  userSignUp(parameters) {
    const {
      userId,
      email,
      groupId,
      source,
      homeCity,
    } = parameters;

    if (!userId) {
      log.error('Signup failed. No user id provided');
      return Promise.resolve();
    }

    window.braze.changeUser(userId);
    log.debug(`Braze userId changed [${userId}]`);

    const user = window.braze.getUser();

    if (user && email) {
      user.setEmail(email);
    }

    if (user && homeCity) {
      user.setHomeCity(homeCity);
    }

    if (user && groupId && source) {
      log.debug(`Braze setting groupId [${groupId}]`);

      const eventName = window.braze.User.NotificationSubscriptionTypes.SUBSCRIBED;
      user.setEmailNotificationSubscriptionType(eventName);

      log.debug('Braze userSignUp setEmailNotificationSubscriptionType');

      window.braze.logCustomEvent('added_to_subscription_group', {
        subscription_group_id: groupId,
      });
      user.setCustomUserAttribute('signup-source', source);
      window.braze.requestImmediateDataFlush();
      log.debug('Braze signup success');
    }

    return Promise.resolve();
  }

  /**
   * Set user and subscribe to newsletter.
   * @param {String} userId - new User id.
   * @param {String} groupId - Id of list to sign up for.
   * @param {Source} source - The source from where the user signed up.
   * @returns {Promise}
   */
  userSignUpByHash(userId, groupId, source) {
    if (!userId) {
      log.error('Signup failed. No user id provided');
      return Promise.resolve();
    }

    window.braze.changeUser(userId);
    log.debug(`Braze userId changed [${userId}]`);

    const user = window.braze.getUser();
    if (user && groupId && source) {
      log.debug(`Braze setting groupId [${groupId}]`);

      const eventName = window.braze.User.NotificationSubscriptionTypes.SUBSCRIBED;
      user.setEmailNotificationSubscriptionType(eventName);

      log.debug('Braze userSignUpByHash setEmailNotificationSubscriptionType');
      window.braze.logCustomEvent('added_to_subscription_group', {
        subscription_group_id: groupId,
      });
      user.setCustomUserAttribute('signup-source', source);

      window.braze.requestImmediateDataFlush();
      log.debug('Braze signup success');
    }

    return Promise.resolve();
  }
}

export default Braze;



// WEBPACK FOOTER //
// ./src/Braze.js